// src/views/Clientes/ActualizacionLimiteDeCredito/AprobarRechazar.jsx
import React, { useEffect, useState, useMemo } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
import { getUsuario } from "../../../utils/auth";
import { formatoPy, parsePy, isPyNumeric } from "../../../utils/formatoNumeros";

export default function AprobarRechazar({
  codigo,
  onClose = () => {},
  onResolved = () => {},   // 🔥 nuevo callback para refrescar el padre
}) {
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");

  // Campos editables
  const [estadoId, setEstadoId] = useState(null); // 3 = APROBADA, 4 = RECHAZADA
  const [valorAprobado, setValorAprobado] = useState(""); // crudo sin puntos
  const [motivoRechazo, setMotivoRechazo] = useState("");

  // Usuario aprobación y fecha
  const usuarioAprobacion = getUsuario() || "";
  const fechaAprobacion = useMemo(
    () => new Date().toLocaleDateString("es-PY"),
    []
  );

  // ============================
  //   CARGA INICIAL
  // ============================
  useEffect(() => {
    if (!codigo) return;

    setCargando(true);
    setError("");

    apiFetchJson(apiRoutes.limiteCredito.detail(codigo))
      .then((res) => {
        setData(res);

        setEstadoId(null);
        setValorAprobado(
          res.valorMontoAprobado != null
            ? res.valorMontoAprobado
            : res.valorMontoSolicitado
        );
        setMotivoRechazo(res.textoMotivoRechazo || "");
      })
      .catch((err) => {
        console.error("Error cargando solicitud:", err);
        setError("No se pudieron cargar los datos.");
      })
      .finally(() => setCargando(false));
  }, [codigo]);

  // ============================
  // GUARDAR
  // ============================
  const handleGuardar = async () => {
    if (!estadoId) {
      window.overlay?.open({
        mode: "error",
        title: "Validación",
        message: "Debe seleccionar un estado.",
      });
      return;
    }

    if (estadoId === 3 && !valorAprobado) {
      window.overlay?.open({
        mode: "error",
        title: "Validación",
        message: "Debe ingresar el monto aprobado.",
      });
      return;
    }

    if (estadoId === 4 && !motivoRechazo.trim()) {
      window.overlay?.open({
        mode: "error",
        title: "Validación",
        message: "Debe ingresar el motivo del rechazo.",
      });
      return;
    }

    const payload = {
      estadoId,
      valorMontoAprobado: estadoId === 3 ? Number(valorAprobado) : null,
      textoMotivoRechazo: estadoId === 4 ? motivoRechazo : null,
      codigoUsuarioAprobacion: usuarioAprobacion,
    };

    try {
      await appFetchJson(
        apiRoutes.limiteCredito.resolver(codigo),
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        {
          showLoading: true,
          showSuccess: true,
          successTitle: "Operación exitosa",
          successMessage:
            estadoId === 3
              ? "Solicitud aprobada correctamente."
              : "Solicitud rechazada correctamente.",
          onSuccessClose: () => {
            onResolved();   // 🔥 refresca el padre
            onClose();      // cierra modal
          },
        }
      );
    } catch (e) {
      console.error("Error resolviendo solicitud:", e);
    }
  };

  // Helpers visuales
  const fmtMonto = (v) =>
    v == null ? "-" : Number(v).toLocaleString("es-PY");

  const fmtFecha = (v) => {
    if (!v) return "-";
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? String(v) : d.toLocaleString("es-PY");
  };

  // ============================
  //   RENDER
  // ============================
  if (cargando) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body text-center py-10">Cargando…</div>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body text-center text-red-600 py-10">
            {error || "Error desconocido"}
          </div>
          <div className="modal-footer text-right">
            <button className="btn-fil" onClick={onClose}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop">
      <div className="modal-panel-lg">
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">Aprobación / Rechazo</h2>
          <button className="modal-close-x" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="modal-body space-y-4">
          <Fila label="Cliente" value={data.clienteNombre} />
          <Fila label="Monto solicitado" value={fmtMonto(data.valorMontoSolicitado)} />
          <Fila label="Fecha solicitud" value={fmtFecha(data.dataFechaSolicitud)} />
          <Fila label="Motivo solicitud" value={data.textoMotivoSolicitud} />
          <Fila label="Usuario solicitud" value={data.codigoUsuarioSolicitud} />
          <Fila label="Límite anterior" value={fmtMonto(data.valorLimiteAnterior)} />

          {/* ESTADO */}
          <div className="form-row">
            <div className="form-label">Estado:</div>
            <div>
              <select
                className="input"
                value={estadoId || ""}
                onChange={(e) => setEstadoId(Number(e.target.value))}
              >
                <option value="">-- Seleccionar --</option>
                <option value={3}>APROBADA</option>
                <option value={4}>RECHAZADA</option>
              </select>
            </div>
          </div>

          {/* MONTO APROBADO */}
          {estadoId === 3 && (
            <div className="form-row">
              <div className="form-label">Monto aprobado:</div>
              <div>
                <input
                  type="text"
                  className="input"
                  value={formatoPy(valorAprobado)}
                  onChange={(e) => {
                    const txt = e.target.value;
                    if (!isPyNumeric(txt)) return;

                    const raw = parsePy(txt);
                    setValorAprobado(raw);
                  }}
                />
              </div>
            </div>
          )}

          {/* MOTIVO RECHAZO */}
          {estadoId === 4 && (
            <div className="form-row">
              <div className="form-label">Motivo rechazo:</div>
              <div>
                <textarea
                  className="input"
                  value={motivoRechazo}
                  onChange={(e) => setMotivoRechazo(e.target.value)}
                />
              </div>
            </div>
          )}

          <Fila label="Usuario aprobación" value={usuarioAprobacion} />
          <Fila label="Fecha aprobación" value={fechaAprobacion} />
        </div>

        <div className="modal-footer flex justify-end gap-2">
          <button className="btn-fil" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn-fil" onClick={handleGuardar}>
            Guardar
          </button>
        </div>
      </div>
    </div>
  );
}

function Fila({ label, value }) {
  return (
    <div className="form-row">
      <div className="form-label">{label}:</div>
      <div className="value">{value ?? "-"}</div>
    </div>
  );
}
